import { createPortal } from 'react-dom';
import { useParams } from 'react-router';
import { useEffect, useState } from 'react';

declare global {
    interface Window {
        renderForm?: (formCode: string, elementId: string) => void;
    }
}

const SCRIPT_IDS = {
    observer: 'embed-observer-script',
    formScript: 'embed-form-script'
};

const EmbeddedForm = () => {
    const { code } = useParams<{ code: string }>();
    const [ready, setReady] = useState(false);
    const elementId = `my-form-${code}`;

    useEffect(() => {
        if (!code) return;

        const cleanupScripts = () => {
            Object.values(SCRIPT_IDS).forEach((id) => {
                const existingScript = document.getElementById(id);
                if (existingScript) {
                    existingScript.remove();
                }
            });

            const container = document.getElementById(elementId);
            if (container) container.innerHTML = '';
        };

        const injectObserverScript = () => {
            const script = document.createElement('script');
            script.id = SCRIPT_IDS.observer;
            script.textContent = `
              const observer = new MutationObserver(() => {
                if (typeof renderForm === "function") {
                  renderForm("${code}", "my-form-${code}");
                  observer.disconnect();
                }
              });
              observer.observe(document, { childList: true, subtree: true });
            `;
            document.body.appendChild(script);
        };

        const injectFormScript = () => {
            const script = document.createElement('script');
            script.id = SCRIPT_IDS.formScript;
            script.src = `${process.env.REACT_APP_API_BASE_URL}embed-form-script.js`;
            script.defer = true;
            document.body.appendChild(script);
        };
        const fetchAndInjectForm = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}render-form`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ formCode: code })
                });

                if (!response.ok) {
                    console.error('Failed to load form HTML.');
                    return;
                }

                const formHtml = await response.text();
                const container = document.getElementById(elementId);
                if (!container) return;
                container.innerHTML = formHtml;
            } catch (error) {
                console.error('Error fetching form HTML:', error);
            }
        };
        cleanupScripts();
        injectObserverScript();
        injectFormScript();
        fetchAndInjectForm();
        setReady(true);

        // eslint-disable-next-line consistent-return
        return () => {
            cleanupScripts();
        };
    }, [code]);
    const portalRoot = document.getElementById('form-portal-root');
    if (!portalRoot || !ready) return null;

    return createPortal(<div id={elementId} />, portalRoot);
};

export default EmbeddedForm;